import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, Router, withNavigationErrorHandler } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ApolloClient, InMemoryCache } from '@apollo/client';
import { AppConfigService } from '@app/app-config.service';
import { AppComponent } from '@app/app.component';
import { tacs3Absence } from '@app/custom-icons/absence.icon';
import { tacs3AccidentIllness } from '@app/custom-icons/accident-illness.icon';
import { tacs3Customer } from '@app/custom-icons/customer.icon';
import { tacs3Education } from '@app/custom-icons/education.icon';
import { tacs3FurtherEducation } from '@app/custom-icons/further-education.icon';
import { tacs3Organisation } from '@app/custom-icons/organisation.icon';
import { tacs3PaidVacation } from '@app/custom-icons/paid-vacation.icon';
import { tacs3Record } from '@app/custom-icons/record.icon';
import { tacs3TacsIcon } from '@app/custom-icons/tacs.icon';
import { tacs3UnpaidVacation } from '@app/custom-icons/unpaid-vacation.icon';
import { tacs3Vacation } from '@app/custom-icons/vacation.icon';
import { GraphQLMainDataQueryService, GraphQLRole } from '@app/graphql/.generated';
import { DefaultOAuthInterceptor } from '@app/oauth.interceptor';
import { appRoutes } from '@app/routes';
import { preloadPersistedState } from '@app/shared/store/persisted-state.feature';
import { MainDataStore } from '@app/store/main-data.store';
import { dateIsWithinValidityPeriod } from '@app/util';

import { environment } from '@environment/environment';
import { FaConfig, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import {
  faAdd,
  faAngleDown,
  faAngleLeft,
  faAngleUp,
  faArrowDown,
  faArrowDownRight,
  faArrowDownShortWide,
  faArrowLeft,
  faArrowRight,
  faArrowUp,
  faArrowUpArrowDown,
  faArrowUpRight,
  faArrowUpShortWide,
  faBaby,
  faBarChart,
  faBars,
  faBed,
  faBell,
  faBells,
  faBriefcase,
  faBuilding,
  faBuildings,
  faCalculator,
  faCalendar,
  faCalendarArrowDown,
  faCalendarCheck,
  faCalendarDay,
  faChartUser,
  faCheck,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faChild,
  faClipboardUser,
  faClock,
  faClose,
  faCopy,
  faCross,
  faCubes,
  faDash,
  faDesktopMedical,
  faEdit,
  faEllipsisVertical,
  faFileCsv,
  faFileDownload,
  faFilePdf,
  faFileUpload,
  faFilter,
  faGear,
  faGraduationCap,
  faHandHoldingHand,
  faHeartPulse,
  faHospital,
  faHouseMedical,
  faHyphen,
  faInfoCircle,
  faKey,
  faKitMedical,
  faLanguage,
  faLayerGroup,
  faLeft,
  faLineColumns,
  faListCheck,
  faListDropdown,
  faMagicWandSparkles,
  faMagnifyingGlass,
  faMessageSmile,
  faMinus,
  faNotesMedical,
  faPartyHorn,
  faPassport,
  faPerson,
  faPersonPregnant,
  faPills,
  faPlane,
  faPlus,
  faQuestionCircle,
  faRectangleHistory,
  faRight,
  faRing,
  faRocketLaunch,
  faRotate,
  faRotateLeft,
  faRotateRight,
  faSave,
  faSearch,
  faSend,
  faShield,
  faSignOut,
  faSitemap,
  faSort,
  faSpinner,
  faStar,
  faStethoscope,
  faStop,
  faSyringe,
  faTable,
  faTag,
  faTrash,
  faTrashUndo,
  faTriangleExclamation,
  faTruckMoving,
  faUmbrellaBeach,
  faUser,
  faUserGroup,
  faUserRobot,
  faUsersMedical,
  faWifiSlash,
  faWindowMaximize,
  faWindowMinimize,
  faXmark,
  faFamilyPants,
  faFaceThermometer,
  faPersonFalling,
  faBriefcaseMedical,
  faMoon,
  faDesktop,
  faSun,
  faArrowsSplitUpAndLeft,
  faBroom,
  faHouse,
  faUserTie,
  faClockRotateLeft,
  faCloudMoon,
  faPhone,
  faUserGraduate,
  faChalkboardUser,
  faBolt,
  faClipboardCheck,
  faPeopleGroup,
  faBookOpen,
  faBookOpenReader,
  faCalendarCircleUser,
  faUserClock,
  faBan,
} from '@fortawesome/pro-duotone-svg-icons';
import { faStar as faStarRegular } from '@fortawesome/pro-regular-svg-icons';
import { patchState } from '@ngrx/signals';
import * as Sentry from '@sentry/angular';
import { AuthConfig, OAuthService, provideOAuthClient } from 'angular-oauth2-oidc';
import { Apollo, APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { ConfirmationService, FilterService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { DialogService } from 'primeng/dynamicdialog';
import { lastValueFrom } from 'rxjs';
import { tacsPreset } from './primeng-preset';
import { translation } from './primeng-translation';

if (environment.production) {
  enableProdMode();
}

const initializeAppFactory = async (): Promise<unknown> => {
  const oauthService = inject(OAuthService);
  const configService = inject(AppConfigService);
  const store = inject(MainDataStore);
  const graphQLMainDataQueryService = inject(GraphQLMainDataQueryService);
  const locale = inject(LOCALE_ID);
  const faIconLibrary = inject(FaIconLibrary);
  const faConfig = inject(FaConfig);
  const filterService = inject(FilterService);

  configureFontAwesome(faConfig, faIconLibrary);

  filterService.register('includes', (value: unknown[], filter?: unknown): boolean => {
    if (!filter) {
      return true;
    }
    return value.includes(filter);
  });

  const appConfig = await configService.reload();
  // eslint-disable-next-line no-console
  console.info('Server Version:', appConfig.serverVersion);

  if (appConfig.auth === undefined) {
    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
    return Promise.reject('The server did not provide a valid Auth config.');
  }

  let search = '';

  if (window.location.search.startsWith('?caseId=')) {
    search = window.location.search;
    const indexOfAmpersand = search.indexOf('&');
    if (indexOfAmpersand > -1) {
      search = search.substring(0, indexOfAmpersand);
    }
  }
  const authCodeFlowConfig: AuthConfig = {
    issuer: appConfig.auth.issuerUrl,
    redirectUri: window.location.origin + window.location.pathname + search,
    clientId: appConfig.auth.clientId,
    requireHttps: appConfig.auth.requireTls,
    responseType: 'code',
    scope: 'openid profile email offline_access',
    postLogoutRedirectUri: window.location.origin + window.location.pathname,
    timeoutFactor: 0.75,
    sessionChecksEnabled: false,
    clockSkewInSec: 90,
  };

  oauthService.configure(authCodeFlowConfig);
  oauthService.setupAutomaticSilentRefresh();
  oauthService.setStorage(window.localStorage);

  const loginSucceeded = await tryLoadDiscoveryDocumentAndTryLoginWithRetries(oauthService, 5);
  if (!loginSucceeded) {
    alert(
      $localize`Es konnte keine Verbindung zum Authentifizierungsserver hergestellt werden, bitte kontaktiere support@rodix.ch.`
    );
    return;
  }

  if (!oauthService.hasValidIdToken() || !oauthService.hasValidAccessToken()) {
    if (!environment.production) {
      window.localStorage.clear();
    }
    oauthService.initLoginFlow();
    return new Promise(() => {});
  }

  if (oauthService.hasValidIdToken() && oauthService.hasValidAccessToken()) {
    const identityClaims = oauthService.getIdentityClaims();

    configureSentryUser(identityClaims);
    configureSentry(configService);

    const result = await lastValueFrom(graphQLMainDataQueryService.fetch());
    const roles = (identityClaims.roles as string[] | undefined) ?? [];

    const tenantId =
      result.data?.me?.employments
        .flatMap((e) => e.mutations)
        .filter((m) => dateIsWithinValidityPeriod(new Date(), m.validityPeriod))
        .map((m) => m.organisationalUnit.tenantId)[0] ?? result.data?.tenants[0]?.id;
    patchState(store, {
      // The Administrator domain role grants the same application admin rights as the Keycloak
      // admin realm role, without being a Keycloak admin.
      admin: roles.includes('admin') || result.data?.me?.role === GraphQLRole.Administrator,
      language: locale,
      tenant: result.data?.tenants.find((t) => t.id === tenantId),
      tenants: result.data?.tenants,
      me: result.data?.me ?? null,
    });
    store.loadAdminData();
    return;
  }
  throw new Error('Could not load main data');
};

void preloadPersistedState().then(() =>
  bootstrapApplication(AppComponent, {
    providers: [
      importProvidersFrom(
        ServiceWorkerModule.register('ngsw-worker.js', {
          enabled: environment.production,
          // Register once the app is stable (or after 30s at the latest) instead of
          // during bootstrap, where registration is more likely to fail on slow or
          // flaky connections.
          registrationStrategy: 'registerWhenStable:30000',
        })
      ),
      {
        provide: HTTP_INTERCEPTORS,
        useClass: DefaultOAuthInterceptor,
        multi: true,
      },
      provideHttpClient(withFetch(), withInterceptorsFromDi()),
      providePrimeNG({
        theme: {
          preset: tacsPreset,
          options: {
            darkModeSelector: 'none',
            cssLayer: {
              name: 'primeng',
              order: 'theme, base, primeng',
              darkModeSelector: '.tacs-dark-mode',
            },
          },
        },
        translation: translation,
        ripple: true,
        // PrimeUI community tier key, type "dev", expires 2027-08-11. Renewal requires
        // re-confirming eligibility, and the community tier is limited to individuals,
        // non-profits and small organisations, so whether tacs3 qualifies is an open
        // question rather than a settled one. The key is in the source because it ships
        // in the bundle regardless, not because sharing one is the intended model.
        license:
          'eyJpZCI6ImZlZmY2MTAwLWVmY2EtNDU5ZS1iMmNiLTFhZWFhMzI4MDQxYiIsInByb2R1Y3QiOiJwcmltZXVpIiwidGllciI6ImNvbW11bml0eSIsInR5cGUiOiJkZXYiLCJpYXQiOjE3ODY0MzE3NDUsImV4cCI6MTgxNzk2Nzc0NX0.cN14SggWGNvKHy30bBz27sK7vFGHypsxNJiSlqYSfW5sU1c3kuuipFKzEHj5cE9Scj1R6mGUCfCMIjf3-buvDw',
      }),
      Apollo,
      {
        provide: APOLLO_OPTIONS,
        useFactory: createApollo,
        deps: [HttpLink],
      },
      ConfirmationService,
      MessageService,
      DialogService,
      DatePipe,
      AppConfigService,
      provideAppInitializer(initializeAppFactory),
      ...(environment.production
        ? [
            {
              provide: ErrorHandler,
              useValue: Sentry.createErrorHandler({
                showDialog: true,
              }),
            },
            {
              provide: Sentry.TraceService,
              deps: [Router],
            },
          ]
        : []),
      provideRouter(
        appRoutes,
        withNavigationErrorHandler((error) => {
          if (isChunkLoadingError(error)) {
            window.location.assign(window.location.href);
          }
        })
      ),
      provideOAuthClient(),
    ],
  })
);

function configureFontAwesome(faConfig: FaConfig, faIconLibrary: FaIconLibrary): void {
  faConfig.fixedWidth = true;
  faConfig.defaultPrefix = 'fad';
  faIconLibrary.addIcons(
    faMessageSmile,
    faSend,
    faPerson,
    faBuilding,
    faGraduationCap,
    faPlane,
    faTrash,
    faTrashUndo,
    faSpinner,
    faEdit,
    faCheck,
    faAdd,
    faQuestionCircle,
    faLeft,
    faRight,
    faHyphen,
    faRocketLaunch,
    faPartyHorn,
    faAngleLeft,
    faAngleUp,
    faAngleDown,
    faFileDownload,
    faFilePdf,
    faFileUpload,
    faMagnifyingGlass,
    faSearch,
    faFileCsv,
    faPlus,
    faMinus,
    faSave,
    faClose,
    faBuilding,
    faSignOut,
    faChevronDown,
    faChevronRight,
    faChevronUp,
    faChevronLeft,
    faCheck,
    faClock,
    faUserClock,
    faDesktopMedical,
    faLanguage,
    faWifiSlash,
    faBars,
    faKey,
    faCalendarDay,
    faCalendar,
    faNotesMedical,
    faTable,
    faSitemap,
    faUser,
    faUserGroup,
    faListDropdown,
    faListCheck,
    faLineColumns,
    faClipboardUser,
    faCalculator,
    faTriangleExclamation,
    faBuildings,
    faEllipsisVertical,
    faLayerGroup,
    faUsersMedical,
    faHandHoldingHand,
    faStop,
    faInfoCircle,
    faMagicWandSparkles,
    tacs3Record,
    tacs3Absence,
    tacs3AccidentIllness,
    tacs3Customer,
    tacs3Education,
    tacs3FurtherEducation,
    tacs3Organisation,
    tacs3PaidVacation,
    tacs3UnpaidVacation,
    tacs3Vacation,
    tacs3TacsIcon,
    faArrowDown,
    faArrowDownRight,
    faArrowRight,
    faArrowLeft,
    faArrowUp,
    faArrowUpRight,
    faArrowUpShortWide,
    faArrowDownShortWide,
    faArrowUpArrowDown,
    faCalendarArrowDown,
    faChartUser,
    faCalendarCheck,
    faUserRobot,
    faCubes,
    faDash,
    faTag,
    faQuestionCircle,
    faFilter,
    faXmark,
    faWindowMaximize,
    faWindowMinimize,
    faGear,
    faRotate,
    faCopy,
    faRectangleHistory,
    faPassport,
    faCalculator,
    faRotateLeft,
    faRotateRight,
    faBarChart,
    faStar,
    faStarRegular,
    faBells,
    faBed,
    faSort,
    faBell,
    faBriefcase,
    faHeartPulse,
    faHospital,
    faHouseMedical,
    faKitMedical,
    faPills,
    faStethoscope,
    faSyringe,
    faUmbrellaBeach,
    faBaby,
    faCross,
    faChild,
    faShield,
    faTruckMoving,
    faRing,
    faPersonPregnant,
    faFamilyPants,
    faFaceThermometer,
    faPersonFalling,
    faBriefcaseMedical,
    faMoon,
    faDesktop,
    faSun,
    faArrowsSplitUpAndLeft,
    faBroom,
    faHouse,
    faUserTie,
    faClockRotateLeft,
    faCloudMoon,
    faPhone,
    faUserGraduate,
    faChalkboardUser,
    faBolt,
    faClipboardCheck,
    faPeopleGroup,
    faBookOpen,
    faBookOpenReader,
    faCalendarCircleUser,
    faSort,
    faBan
  );
}

function configureSentry(configService: AppConfigService): void {
  Sentry.setTag('customer', configService.configuration?.customer.name);
  Sentry.init({
    dsn: configService.configuration?.sentryDsn,
    environment: configService.configuration?.environment,
    release: configService.configuration?.serverVersion,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
      Sentry.httpClientIntegration(),
    ],

    beforeSend(event) {
      const message = event.message ?? event.exception?.values?.[0]?.value ?? '';
      if (message.includes('Error refreshing token')) {
        return null;
      }
      // NG05604: the Angular service worker logs a console error when its
      // registration fails. This happens for transient reasons (offline,
      // flaky connection) and the app works without the service worker,
      // so there is nothing actionable to report.
      if (message.includes('NG05604')) {
        return null;
      }
      return event;
    },

    tracesSampleRate: 0.1,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^\//],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
  });
}

function configureSentryUser(identityClaims: Record<string, unknown>): void {
  Sentry.setUser({
    id: identityClaims.sub as string,
    username: identityClaims.preferred_username as string,
    name: identityClaims.name as string,
    email: identityClaims.email as string,
  });
}

function createApollo(httpLink: HttpLink): ApolloClient.Options {
  return {
    link: httpLink.create({ uri: '/graphql' }),
    cache: new InMemoryCache({ resultCaching: false }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore',
      },
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
    },
  };
}

async function tryLoadDiscoveryDocumentAndTryLoginWithRetries(
  oauthService: OAuthService,
  retries = 60
): Promise<boolean> {
  let attempts = 0;

  while (attempts < retries) {
    try {
      return await oauthService.loadDiscoveryDocumentAndTryLogin();
    } catch (error) {
      attempts++;
      // eslint-disable-next-line no-console
      console.error(`Login attempt ${attempts} failed.`, error);

      if (attempts >= retries) {
        return false;
      }

      await new Promise((resolve) => setTimeout(resolve, Math.min(1000 * Math.pow(2, attempts - 1)), 10 * 1000));
    }
  }

  return false;
}

function isChunkLoadingError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return message.includes('Failed to fetch dynamically imported module') || message.includes('ChunkLoadError');
}
